import React, { useMemo, useState } from 'react';
import { Filter as FilterIcon } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { buildRoundsWithRows, countActiveFilters } from './scheduleRows';
import RoundSection from './RoundSection';
import MatchDetailModal from '../MatchDetailModal';

const BORDER = 'border-gray-400 dark:border-gray-500';

// A literal port of production's schedule display (ScheduleTab.jsx:1666-1940
// mobile card layout + :1808-1940 desktop table layout), not an original
// design — same two responsive layouts, same round-header/divider/hover
// treatment, same grid-cols-3 name/vs/name shape on mobile and #/Round/Home/
// Score/Away/Status/Actions columns on desktop. Score/live-check-in status
// are dropped since they require live tournament state that doesn't exist
// before a tournament actually runs — Score always reads "vs", and Status
// always reads "Draft" (this schedule isn't submitted anywhere yet — see
// useScheduleBuilder's doc comment). Order/gear icons are real: reordering
// a row within its round (`onReorderRow`) and the per-row/per-round
// Match/Round Settings modal (`onApplyRowOverride`/`onApplyRoundOverride`)
// — see MatchListView for where these come from. Court is shown only when
// `courts` is non-empty — see scheduleRows.js for the auto-assign + override
// resolution that produces each row's `court`.
//
// The one shared bordered "league" container below renders both layouts
// (only one is ever visible at a time, via `md:` breakpoints); each round is
// a RoundSection, each game a MatchRow — collapse state lives here so it
// applies to a round in both layouts even though only one is on screen.
const MatchTable = ({
  rounds,
  orderOverrides,
  onReorderRow,
  courts = [],
  sportType,
  tournamentMatchSettings,
  roundOverrides = {},
  rowOverrides = {},
  onApplyRoundOverride,
  onApplyRowOverride,
  filters = null,
  currentUserId = null,
  onClearFilters,
  tournamentVO = null,
  startScoring,
  openLiveGame,
  canManageThisSchedule,
  updateMatchSchedule,
  deleteMatchRow,
  assignMatchParticipants,
  addMatch,
  fetchAllParticipants,
  participantType,
}) => {
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [detailIndex, setDetailIndex] = useState(null);
  const roundsWithRows = useMemo(
    () => buildRoundsWithRows(rounds, orderOverrides, courts, roundOverrides, rowOverrides, filters, currentUserId),
    [rounds, orderOverrides, courts, roundOverrides, rowOverrides, filters, currentUserId]
  );
  const showCourt = courts.length > 0;
  // Flattened, in display order — this is exactly what Previous/Next in
  // MatchDetailModal steps through: THIS list's own already-filtered rows,
  // tagged with which round each came from since a flattened row no longer
  // has its round object handy the way RoundSection's per-round rendering
  // does.
  const allRows = useMemo(
    () => roundsWithRows.flatMap(({ round, rows }) => rows.map((row) => ({ ...row, roundLabel: round.label }))),
    [roundsWithRows]
  );
  const openDetailForRow = (row) => setDetailIndex(allRows.findIndex((r) => r.key === row.key));
  const hasActiveFilters = countActiveFilters(filters) > 0;

  const toggleRound = (roundId) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(roundId)) next.delete(roundId);
      else next.add(roundId);
      return next;
    });
  };

  // A row moves up/down within its own round only — swaps it with whichever
  // neighbor it's heading toward and hands the resulting key order back up
  // to whoever owns `orderOverrides` (see useScheduleBuilder's `reorderRow`),
  // rather than holding reordered state locally, since `rounds` here is
  // freshly rebuilt from the generator's output on most renders (tie format
  // changes, view toggles, ...) and would silently discard a local reorder.
  const moveRow = (round, rows, index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= rows.length) return;
    const newOrder = rows.map((r) => r.key);
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    onReorderRow?.(round.id, newOrder);
  };

  const roundSectionProps = (round, rows) => ({
    round,
    rows,
    showCourt,
    courts,
    sportType,
    tournamentMatchSettings,
    roundOverride: roundOverrides[round.id],
    onApplyRoundOverride,
    onApplyRowOverride,
    onOpenDetail: openDetailForRow,
    isCollapsed: collapsed.has(round.id),
    onToggle: () => toggleRound(round.id),
    onMoveRow: onReorderRow && ((index, direction) => moveRow(round, rows, index, direction)),
    tournamentVO,
    currentUserId,
    canManageThisSchedule,
    updateMatchSchedule,
    deleteMatchRow,
    assignMatchParticipants,
    addMatch,
    fetchAllParticipants,
    participantType,
    startScoring,
    openLiveGame,
  });

  if (roundsWithRows.length === 0 && hasActiveFilters) {
    return (
      <div className={`flex flex-col items-center gap-3 rounded-lg border border-dashed ${BORDER} px-6 py-10 text-center`}>
        <FilterIcon size={20} className={theme.textMuted} />
        <p className={`text-sm ${theme.textMuted}`}>No matches for the current filters.</p>
        {onClearFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${theme.buttonGhost} border ${theme.border}`}
          >
            Clear filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`overflow-hidden rounded-lg border ${BORDER}`}>
      <div className="divide-y divide-gray-300 dark:divide-gray-600 md:hidden">
        {roundsWithRows.map(({ round, rows }) => (
          <RoundSection key={round.id} variant="mobile" {...roundSectionProps(round, rows)} />
        ))}
      </div>

      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <TableHead showCourt={showCourt} />
          <tbody>
            {roundsWithRows.map(({ round, rows }) => (
              <RoundSection key={round.id} variant="desktop" {...roundSectionProps(round, rows)} />
            ))}
          </tbody>
        </table>
      </div>

      <MatchDetailModal
        isOpen={detailIndex != null}
        onClose={() => setDetailIndex(null)}
        rows={allRows}
        index={detailIndex}
        onPrev={() => setDetailIndex((i) => Math.max(0, i - 1))}
        onNext={() => setDetailIndex((i) => Math.min(allRows.length - 1, i + 1))}
        tournamentVO={tournamentVO}
        currentUserId={currentUserId}
        startScoring={startScoring}
        openLiveGame={openLiveGame}
      />
    </div>
  );
};

const TableHead = ({ showCourt }) => {
  const { theme } = useTheme();
  return (
    <thead>
      <tr className={`border-b-2 ${BORDER} ${theme.backgroundSecondary}`}>
        <th className={`w-10 border-r px-3 py-2 text-left text-xs font-semibold ${theme.textMuted} ${BORDER}`}>#</th>
        <th className={`w-32 border-r px-3 py-2 text-left text-xs font-semibold ${theme.textMuted} ${BORDER}`}>Round</th>
        <th className={`border-r px-3 py-2 text-left text-xs font-semibold ${theme.textMuted} ${BORDER}`}>Home</th>
        <th className={`w-20 border-r px-3 py-2 text-center text-xs font-semibold whitespace-nowrap ${theme.textMuted} ${BORDER}`}>Score</th>
        <th className={`border-r px-3 py-2 text-left text-xs font-semibold ${theme.textMuted} ${BORDER}`}>Away</th>
        {showCourt && (
          <th className={`w-24 border-r px-3 py-2 text-center text-xs font-semibold ${theme.textMuted} ${BORDER}`}>Court</th>
        )}
        <th className={`w-20 border-r px-3 py-2 text-center text-xs font-semibold ${theme.textMuted} ${BORDER}`}>Status</th>
        <th className={`w-16 px-3 py-2 text-center text-xs font-semibold ${theme.textMuted}`}>Order</th>
      </tr>
    </thead>
  );
};

export default MatchTable;
